import { Component, Input } from '@angular/core';
import { ImagingRequest, ScanRegion } from '../models/imaging-requests.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ImagingRequestDetailsModalComponent } from '../imaging-request-details/imaging-request-details-modal.component';
import { ScanRegionStatus, ImagingRequestStatus } from '../models/enums';
import { ClassToDisplayMapping } from '../models/class-display-mapping';
import { HeaderBadge } from '../models/header-badge.model';
import { ImagingRequestFormComponent } from '../imaging-request-form/imaging-request-form.component';
import { SnackbarService } from '@app/services/snackbar.sevice';

@Component({
    selector: 'app-imaging-requests-tile',
    templateUrl: './imaging-requests-tile.component.html',
    styleUrl: './imaging-requests-tile.component.scss'
})
export class ImagingRequestsTileComponent {
    scanRegions: BadgedScanRegion[];
    headerBadges: HeaderBadge[];

    tileColor: BorderColor = 'border-lighter-dark';

    @Input()
    set data(request: ImagingRequest) {
        this.assignScanRegions(request.scanRegions);
        this.headerBadges =
            request.headerBadgeClasses?.map(badgeClass => ({
                class: badgeClass,
                display: ClassToDisplayMapping.get(badgeClass)
            })) ?? [];
        this.setTileColorFromStatus(request.status);
        this._data = request;
    }
    get data(): ImagingRequest {
        return this._data;
    }

    @Input()
    userCanEditImagingRequests: boolean = false;

    private _data: ImagingRequest;

    constructor(private readonly snackbarService: SnackbarService, private readonly dialog: MatDialog) {}

    private static readonly _statusesToHideWhenImagesReceived: Set<ScanRegionStatus> = new Set<ScanRegionStatus>([
        ScanRegionStatus.Requested,
        ScanRegionStatus.Approved
    ]);

    private assignScanRegions(scanRegions: ScanRegion[]) {
        const hasOrderScanRegions =
            scanRegions.findIndex(
                region =>
                    region.status === ScanRegionStatus.OrderPending ||
                    region.status === ScanRegionStatus.ActionNeeded ||
                    region.status === ScanRegionStatus.Completed
            ) >= 0;
        this.scanRegions = scanRegions
            .filter(region => region.status != ScanRegionStatus.Cancelled)
            .filter(region => !hasOrderScanRegions || !ImagingRequestsTileComponent._statusesToHideWhenImagesReceived.has(region.status))
            .map(region => ({ ...region, badge: this.getScanRegionBadge(region.status) }));
    }

    private setTileColorFromStatus(status: string) {
        switch (status) {
            case ImagingRequestStatus.ActionNeeded:
                this.tileColor = 'border-warning';
                break;
            case ImagingRequestStatus.ReportCompleted:
            case ImagingRequestStatus.Cancelled:
                this.tileColor = 'border-success';
                break;
            default:
                this.tileColor = 'border-lighter-dark';
                break;
        }
    }

    getScanRegionBadge(status: ScanRegionStatus): string {
        switch (status) {
            case ScanRegionStatus.Requested:
                return '';
            case ScanRegionStatus.Approved:
                return 'grey-checkmark';
            case ScanRegionStatus.Declined:
                return 'grey-cross';
            case ScanRegionStatus.OrderPending:
                return 'grey-checkmark';
            case ScanRegionStatus.ActionNeeded:
                return 'warning-main';
            case ScanRegionStatus.Completed:
                return 'green-checkmark';
            default:
                return '';
        }
    }

    openDetails(): void {
        this.openDialog(this._data);
    }

    openDialog(request: ImagingRequest) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = '100vw';
        dialogConfig.width = '75vw';
        dialogConfig.maxHeight = '95vh';
        dialogConfig.restoreFocus = true;
        dialogConfig.disableClose = true;
        dialogConfig.backdropClass = 'general-dialog-backdrop';
        dialogConfig.enterAnimationDuration = 0;
        dialogConfig.exitAnimationDuration = 0;
        dialogConfig.autoFocus = false;

        if (request.editableRequest && this.userCanEditImagingRequests) {
            dialogConfig.panelClass = ['imaging-request-form-dialog', 'draggable-dialog-container', 'resize-dialog'];
            dialogConfig.data = request;

            this.dialog
                .open(ImagingRequestFormComponent, dialogConfig)
                .afterClosed()
                .subscribe(result => {
                    if (result?.reopen) {
                        // reopen as readonly
                        this.openDialog(result.data);
                        this.snackbarService.openSnackBar('The request is no longer editable.', SnackbarService.SNACK_BAR_CLASS_ALERT);
                    }
                });
        } else {
            dialogConfig.data = { request: request, userCanEditImagingRequests: this.userCanEditImagingRequests };
            dialogConfig.panelClass = ['imaging-request-details-dialog', 'draggable-dialog-container', 'resize-dialog'];
            if (window.innerWidth < 900) {
                // hack to have modal open in the viewport on mobile screens
                dialogConfig.position = { bottom: '50px' };
            }
            this.dialog
                .open(ImagingRequestDetailsModalComponent, dialogConfig)
                .afterClosed()
                .subscribe(result => {
                    if (result?.reopen) {
                        // reopen as editable
                        this.openDialog(result.data);
                        this.snackbarService.openSnackBar('The request is now editable.', SnackbarService.SNACK_BAR_CLASS_SUCCESS);
                    }
                });
        }
    }
}

type BorderColor = 'border-lighter-dark' | 'border-warning' | 'border-success';

interface BadgedScanRegion {
    badge: string;
    modality: string;
    bodyPart: string;
}
